import React, { useState, useContext, useEffect, useRef, useCallback  } from 'react';
import Layout from '../components/Layout';
import { FaCashRegister, FaCheckCircle, FaArrowLeft, FaUser, FaIdCard, FaQrcode, FaCamera, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import AppContext from '../api/AppContext';

const BalanceRecharge = () => {
  const { user: currentUser, token } = useContext(AppContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [rechargeAmount, setRechargeAmount] = useState('R$ 0,00');
  const [associadoData, setAssociadoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para identificação do associado
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [isScanningTag, setIsScanningTag] = useState(false);
  const [tagData, setTagData] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [cameraError, setCameraError] = useState("");
  
  // Novo estado para o modal de confirmação
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processedAmount, setProcessedAmount] = useState('');
  const [tefStatus, setTefStatus] = useState(null);
  const [tefReferencia, setTefReferencia] = useState(null);
  const [tefOrigem, setTefOrigem] = useState('pinpad');
  const tefPollingRef = useRef(null);
  
  // Referências
  const searchInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const amountInputRef = useRef(null);

  // Cleanup do polling TEF ao desmontar
  useEffect(() => {
    return () => {
      if (tefPollingRef.current) clearInterval(tefPollingRef.current);
    };
  }, []);

  const mockAssociadoData = {
    nome: "João Silva Santos",
    cota: "1234",
    digito: "5",
    documento: "123.456.789-00",
    foto: null
  };

  // =============================================
  // LÓGICA DA CÂMERA E QR CODE (igual ao DebitarPedido)
  // =============================================

  const startCamera = async () => {
    try {
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      setCameraError('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleQRCodeScan = () => {
    setShowQRScanner(true);
    setTimeout(() => {
      startCamera();
    }, 100);
  };

  const closeQRScanner = () => {
    setShowQRScanner(false);
    stopCamera();
    setCameraError("");
  };

  

  // =============================================
  // FUNÇÃO DE NOTIFICAÇÃO MELHORADA
  // =============================================

  const showNotification = (message, type = 'info') => {
    // Criar elemento de notificação
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 15px 20px;
      border-radius: 8px;
      color: white;
      font-weight: bold;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      max-width: 300px;
      word-wrap: break-word;
      transition: all 0.3s ease;
      transform: translateX(400px);
      opacity: 0;
    `;

    // Configurar cores baseadas no tipo
    switch(type) {
      case 'success':
        notification.style.backgroundColor = '#4CAF50';
        break;
      case 'error':
        notification.style.backgroundColor = '#f44336';
        break;
      case 'warning':
        notification.style.backgroundColor = '#ff9800';
        break;
      default:
        notification.style.backgroundColor = '#2196F3';
    }

    notification.textContent = message;
    document.body.appendChild(notification);

    // Animação de entrada
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
      notification.style.opacity = '1';
    }, 100);

    // Remover após 4 segundos
    setTimeout(() => {
      notification.style.transform = 'translateX(400px)';
      notification.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 4000);
  };

  // =============================================
  // INTEGRAÇÃO COM API (igual ao DebitarPedido)
  // =============================================

  // Buscar membro pela tag
  const findMemberByTag = async (tagId) => {
    try {
      const response = await fetch(`https://app.olympicbarbacena.com.br/api/auth/check-tag/${tagId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Membro não encontrado');
      
      const result = await response.json();
      //console.log(result)
      if (result.status === "success" && result.exists && result.user) {
        return {
          id: result.user.id,
          name: result.user.name,
          memberId: result.user.associadoId?.toString(),
          balance: result.user.saldo,
          tagId: result.user.tag,
          cpf: result.user.cpf,
          numeroTitulo: result.user.numeroTitulo,
          foto: result.user.foto,
          role: result.user.role
        };
      } else {
        throw new Error('Tag não associada');
      }
    } catch (error) {
      console.error('Erro na busca por tag:', error);
      throw error;
    }
  };

  // =============================================
  // FUNÇÕES AUXILIARES
  // =============================================

  const handleTagDetected = async (tagId) => {
    //console.log('Processando tag:', tagId);
    setTagData(tagId);
    setIsScanningTag(true);

    try {
      const member = await findMemberByTag(tagId);
      
      if (member) {
        setSelectedMember(member);
        //console.log(member)
        // Preencher dados do associado automaticamente
        setAssociadoData({
          nome: member.name,
          cota: member.memberId,
          digito: "",
          documento: member.cpf || "",
          foto: member.foto
        });
        
        setIsScanningTag(false);
        showNotification(`✅ Associado identificado: ${member.name}`, 'success');
        
        // Avançar para próxima etapa automaticamente
        setTimeout(() => {
          setCurrentStep(2);
        }, 1000);
      } else {
        setIsScanningTag(false);
        showNotification('❌ Tag não associada a nenhum membro', 'error');
      }
    } catch (error) {
      console.error('Erro ao buscar membro:', error);
      setIsScanningTag(false);
      showNotification('❌ Erro ao identificar associado', 'error');
    }
  };

  const selectMemberFromSearch = (member) => {
    setSelectedMember(member);
    setSearchResults([]);
    setMemberSearchQuery("");
    
    // Preencher dados do associado
    setAssociadoData({
      nome: member.name,
      cota: member.memberId,
      digito: "",
      documento: member.cpf || "",
      foto: null
    });
    
    showNotification(`✅ Associado selecionado: ${member.name}`, 'success');
    setCurrentStep(2);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handleScanComplete = () => {
    setIsLoading(true);
    setTimeout(() => {
      setAssociadoData(mockAssociadoData);
      setIsLoading(false);
      setCurrentStep(2);
    }, 2000);
  };

  const handleConfirmAssociado = () => {
    setCurrentStep(3);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Função para formatar o valor monetário
  const formatCurrency = (value) => {
    // Remove tudo que não é número
    let numbers = value.replace(/\D/g, '');
    
    // Se estiver vazio, retorna R$ 0,00
    if (numbers === '') return 'R$ 0,00';
    
    // Converte para número e formata
    let number = parseInt(numbers) / 100;
    
    // Formata como moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para lidar com a mudança no input de valor
  const handleAmountChange = async (e) => {
    let value = e.target.value;
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    //await delay(15000);
    // Se o valor começar com "R$", mantém, senão adiciona
    if (!value.startsWith('R$')) {
      value = 'R$ ' + value.replace(/\D/g, '');
    }
    
    // Remove tudo que não é número, mantendo apenas dígitos
    let numbers = value.replace(/\D/g, '');
    
    // Se estiver vazio, retorna R$ 0,00
    if (numbers === '') {
      setRechargeAmount('R$ 0,00');
      return;
    }
    
    // Converte para número e formata (divide por 100 para considerar centavos)
    let number = parseInt(numbers) / 100;
    
    // Formata como moeda brasileira
    const formattedValue = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
    
    setRechargeAmount(formattedValue);
  };

  // const handleContinue = async () => {
  //   const value = rechargeAmount;
    
  //   // Extrai o valor numérico do formato R$ X.XXX,XX
  //   const numericValue = value.replace('R$', '')
  //     .replace(/\./g, '')
  //     .replace(',', '.')
  //     .trim();

  //   if (!numericValue || parseFloat(numericValue) <= 0 || numericValue === '0.00') {
  //     showNotification('⚠️ Por favor, insira um valor válido para a recarga.', 'warning');
  //     return;
  //   }
  //   // ... resto do código permanece igual
  // };

  // Função para finalizar o processo após confirmação do pagamento
const handlePaymentConfirmed = async () => {
  try {
    setIsLoading(true);
    
    // Extrair valor numérico do processedAmount
    const numericValue = parseFloat(processedAmount.replace('R$', '').replace(/\./g, '').replace(',', '.').trim()/100);
    
    // Mapear método de pagamento para o formato com -maquininha
    const mapPaymentMethod = (method) => {
      switch(method) {
        case 'credit': return 'cartao_credito-maquininha';
        case 'debit': return 'cartao_debito-maquininha';
        case 'cash': return 'dinheiro-maquininha';
        default: return 'dinheiro-maquininha';
      }
    };

    // Preparar dados da recarga
    const rechargeData = {
      userId: selectedMember.id,
      valor: numericValue,
      metodo: mapPaymentMethod(paymentMethod),
      processedBy: {
        id: currentUser.id || currentUser._id,
        name: currentUser.name,
        role: currentUser.role
      },
      timestamp: new Date().toISOString()
    };

    //console.log('📤 Enviando recarga física:', rechargeData);

    const response = await fetch('https://app.olympicbarbacena.com.br/api/minha-conta/recarga-fisica', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rechargeData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
    }

    const result = await response.json();
    //console.log("Recarga processada:", result);
    
    // Fechar modal e mostrar sucesso
    setShowPaymentModal(false);
    
    // Mostrar confirmação com detalhes
    const metodoDisplay = {
      'cartao_credito-maquininha': 'Cartão de Crédito (Maquininha)',
      'cartao_debito-maquininha': 'Cartão de Débito (Maquininha)',
      'dinheiro-maquininha': 'Dinheiro',
      'pix-maquininha': 'PIX'
    };
    
    showNotification(
      `✅ Recarga de R$ ${numericValue.toFixed(2)} processada com sucesso!\n` +
      `Associado: ${selectedMember.name}\n` +
      `Método: ${metodoDisplay[rechargeData.metodo] || rechargeData.metodo}\n` +
      `Saldo anterior: R$ ${result.saldoAnterior?.toFixed(2) || 'N/A'}\n` +
      `Novo saldo: R$ ${result.novoSaldo?.toFixed(2)}`,
      'success'
    );
    
    // Limpar e resetar
    setRechargeAmount('R$ 0,00');
    setSelectedMember(null);
    setAssociadoData(null);
    setCurrentStep(1);
    setProcessedAmount('');
    
  } catch (error) {
    console.error('Erro ao processar recarga:', error);
    setShowPaymentModal(false);
    showNotification(`❌ Erro ao processar recarga: ${error.message}`, 'error');
  } finally {
    setIsLoading(false);
  }
};

  // Input controlado separadamente para evitar rerenders
// Input controlado separadamente para evitar rerenders

  // const handleContinue = async () => {
  //   const value = amountInputRef.current ? amountInputRef.current.value : rechargeAmount;
    
  //   if (!value || parseFloat(value.replace(',', '.')) <= 0) {
  //     alert('Por favor, insira um valor válido para a recarga.');
  //     return;
  //   }

  //   if (!selectedMember) {
  //     alert('Por favor, identifique o associado antes de processar a recarga.');
  //     return;
  //   }

  //   try {
  //     setIsLoading(true);
      
  //     // Preparar dados da recarga - SEM validação bancária
  //     const rechargeData = {
  //       userId: selectedMember.id,
  //       valor: parseFloat(value.replace(',', '.')),
  //       metodo: 'maquininha_fisica', // Método específico para recarga física
  //       processedBy: {
  //         id: currentUser.id || currentUser._id,
  //         name: currentUser.name,
  //         role: currentUser.role
  //       },
  //       timestamp: new Date().toISOString()
  //     };

  //     //console.log('📤 Enviando recarga:', rechargeData);

  //     const response = await fetch('https://app.olympicbarbacena.com.br/api/minha-conta/recarga-fisica', {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(rechargeData),
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
  //     }

  //     const result = await response.json();
  //     //console.log("Recarga processada:", result);
      
  //     alert(`✅ Recarga de R$ ${value} processada com sucesso para ${selectedMember.name}!`);
      
  //     // Limpar e resetar
  //     setRechargeAmount('');
  //     setSelectedMember(null);
  //     setAssociadoData(null);
  //     setCurrentStep(1);
      
  //   } catch (error) {
  //     console.error('Erro ao processar recarga:', error);
  //     alert(`❌ Erro ao processar recarga: ${error.message}`);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };


  // const handleContinue = async () => {
  //   const value = amountInputRef.current ? amountInputRef.current.value : rechargeAmount;
    
  //   if (!value || parseFloat(value.replace(',', '.')) <= 0) {
  //     alert('Por favor, insira um valor válido para a recarga.');
  //     return;
  //   }

  //   if (!selectedMember) {
  //     alert('Por favor, identifique o associado antes de processar a recarga.');
  //     return;
  //   }

  //   try {
  //     setIsLoading(true);
      
  //     // Preparar dados da recarga - SEM validação bancária
  //     const rechargeData = {
  //       userId: selectedMember.id,
  //       valor: parseFloat(value.replace(',', '.')),
  //       metodo: 'maquininha_fisica', // Método específico para recarga física
  //       processedBy: {
  //         id: currentUser.id || currentUser._id,
  //         name: currentUser.name,
  //         role: currentUser.role
  //       },
  //       timestamp: new Date().toISOString()
  //     };

  //     //console.log('📤 Enviando recarga:', rechargeData);

  //     const response = await fetch('https://app.olympicbarbacena.com.br/api/minha-conta/recarga-fisica', {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(rechargeData),
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
  //     }

  //     const result = await response.json();
  //     //console.log("Recarga processada:", result);
      
  //     alert(`✅ Recarga de R$ ${value} processada com sucesso para ${selectedMember.name}!`);
      
  //     // Limpar e resetar
  //     setRechargeAmount('');
  //     setSelectedMember(null);
  //     setAssociadoData(null);
  //     setCurrentStep(1);
      
  //   } catch (error) {
  //     console.error('Erro ao processar recarga:', error);
  //     alert(`❌ Erro ao processar recarga: ${error.message}`);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  // const handleContinue = async () => {
  //   // Obter o valor formatado do input
  //   const value = amountInputRef.current ? amountInputRef.current.value : rechargeAmount;
    
  //   //console.log('Valor capturado:', value); // Debug

  //   // Extrair apenas números e converter para float
  //   const cleanValue = value.replace('R$', '').replace(/\./g, '').replace(',', '.').trim();
  //   const numericValue = parseFloat(cleanValue);

  //   //console.log('Valor limpo:', cleanValue); // Debug
  //   //console.log('Valor numérico:', numericValue); // Debug

  //   // Validar o valor
  //   if (isNaN(numericValue) || numericValue <= 0) {
  //     showNotification('⚠️ Por favor, insira um valor válido para a recarga.', 'warning');
  //     return;
  //   }

  //   if (!selectedMember) {
  //     showNotification('❌ Por favor, identifique o associado antes de processar a recarga.', 'error');
  //     return;
  //   }

  //   // MOSTRAR MODAL DE CONFIRMAÇÃO ANTES DE PROCESSAR
  //   setProcessedAmount(`R$ ${numericValue.toFixed(2)}`);
  //   setShowPaymentModal(true);
  // };

  // Input controlado separadamente para evitar rerenders

  // Iniciar recarga via TEF (PinPad PayGo ou Maquininha Android)
  const handleTefIniciar = async (origem = 'pinpad') => {
    const value = amountInputRef.current ? amountInputRef.current.value : rechargeAmount;
    const cleanValue = value.replace('R$', '').replace(/\./g, '').replace(',', '.').trim();
    const numericValue = parseFloat(cleanValue);

    if (isNaN(numericValue) || numericValue <= 0) {
      showNotification('⚠️ Por favor, insira um valor válido para a recarga.', 'warning');
      return;
    }

    if (!selectedMember) {
      showNotification('❌ Por favor, identifique o associado antes de processar a recarga.', 'error');
      return;
    }

    const userRoles = ['staff', 'admin', 'snackbar', 'caixa'];
    if (!userRoles.includes(currentUser.role)) {
      showNotification('❌ Você não tem permissão para processar recargas.', 'error');
      return;
    }

    const modalidade = origem === 'pinpad' ? 'generico' : 'generico';

    setIsLoading(true);
    setTefOrigem(origem);
    setProcessedAmount(`R$ ${numericValue.toFixed(2)}`);
    setTefStatus('iniciando');

    try {
      const response = await fetch('https://app.olympicbarbacena.com.br/api/minha-conta/recarga-tef', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedMember.id,
          valor: numericValue,
          modalidade,
          origem,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
      }

      const result = await response.json();
      setTefReferencia(result.referencia);
      setTefStatus('aguardando_pinpad');
      setShowPaymentModal(false);
      showNotification(origem === 'android' ? '📱 Aguardando pagamento na maquininha Android' : '🖥️ Aproxime/insira o cartão no PIN PAD', 'info');

      // Polling de status
      tefPollingRef.current = setInterval(() => handleTefConsultar(result.referencia), 4000);

    } catch (error) {
      console.error('Erro ao iniciar recarga TEF:', error);
      setTefStatus('erro');
      showNotification(`❌ Erro ao iniciar recarga TEF: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Consultar status da recarga TEF
  const handleTefConsultar = async (referencia) => {
    try {
      const response = await fetch(`https://app.olympicbarbacena.com.br/api/minha-conta/recarga-tef/${referencia}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const result = await response.json();
      setTefStatus(result.status);

      if (result.status === 'Creditado' || result.statusRecarga === 'paid') {
        clearInterval(tefPollingRef.current);
        showNotification(
          `✅ Recarga de ${result.recarga?.valor ? `R$ ${result.recarga.valor.toFixed(2)}` : ''} aprovada!\n` +
          `Associado: ${selectedMember?.name}`,
          'success'
        );
        setTefReferencia(null);
        setRechargeAmount('R$ 0,00');
        setSelectedMember(null);
        setAssociadoData(null);
        setCurrentStep(1);
        setProcessedAmount('');
        setTefStatus(null);
        window.location.reload();
      } else if (['PagamentoRecusado', 'Expirado', 'Cancelado', 'failed', 'cancelled'].includes(result.status) || result.statusRecarga === 'failed') {
        clearInterval(tefPollingRef.current);
        showNotification('❌ Pagamento não concluído. Tente novamente.', 'error');
        setTefStatus(null);
        setTefReferencia(null);
      }
    } catch (error) {
      console.error('Erro ao consultar recarga TEF:', error);
    }
  };

  // Cancelar recarga TEF
  const handleTefCancelar = async () => {
    if (!tefReferencia) return;
    try {
      await fetch(`https://app.olympicbarbacena.com.br/api/minha-conta/recarga-tef/${tefReferencia}/cancelar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ origem: tefOrigem }),
      });
      clearInterval(tefPollingRef.current);
      setTefStatus(null);
      setTefReferencia(null);
      showNotification('⚠️ Recarga TEF cancelada.', 'warning');
    } catch (error) {
      console.error('Erro ao cancelar recarga TEF:', error);
    }
  };

const handleContinue = async () => {
  const value = amountInputRef.current ? amountInputRef.current.value : rechargeAmount;
  
  // Extrair valor numérico para validação
  const cleanValue = value.replace('R$', '').replace(/\./g, '').replace(',', '.').trim();
  const numericValue = parseFloat(cleanValue);

  if (isNaN(numericValue) || numericValue <= 0) {
    showNotification('⚠️ Por favor, insira um valor válido para a recarga.', 'warning');
    return;
  }

  if (!selectedMember) {
    showNotification('❌ Por favor, identifique o associado antes de processar a recarga.', 'error');
    return;
  }

  // VERIFICAR SE USUÁRIO TEM PERMISSÃO
  const userRoles = ['staff', 'admin', 'snackbar', 'caixa'];
  if (!userRoles.includes(currentUser.role)) {
    showNotification('❌ Você não tem permissão para processar recargas físicas.', 'error');
    return;
  }

  // MOSTRAR MODAL DE CONFIRMAÇÃO ANTES DE PROCESSAR
  setProcessedAmount(`R$ ${numericValue.toFixed(2)}`);
  setShowPaymentModal(true);
};



  // const AmountInput = () => {
  //   const [rechargeAmount, setRechargeAmount] = useState('');

  //   const handleAmountChange = useCallback((e) => {
  //     let value = e.target.value.replace(/\D/g, '');
      
  //     // Converte para centavos
  //     const cents = parseInt(value || '0', 10);
      
  //     // Formata como R$ 0,00
  //     const formatted = `R$ ${(cents / 100).toLocaleString('pt-BR', {
  //       minimumFractionDigits: 2,
  //       maximumFractionDigits: 2
  //     })}`;
      
  //     setRechargeAmount(formatted);
  //   }, []);

  //   const handleFocus = useCallback((e) => {
  //     if (rechargeAmount === 'R$ 0,00') {
  //       setRechargeAmount('');
  //     }
  //   }, [rechargeAmount]);

  //   const handleBlur = useCallback((e) => {
  //     if (!rechargeAmount || rechargeAmount === 'R$') {
  //       setRechargeAmount('R$ 0,00');
  //     }
  //   }, [rechargeAmount]);

  //   return (
  //     <input 
  //       type="text"
  //       placeholder="R$ 0,00"
  //       value={rechargeAmount}
  //       onChange={handleAmountChange}
  //       onFocus={handleFocus}
  //       onBlur={handleBlur}
  //       style={styles.amountInput}
  //     />
  //   );
  // };
  const AmountInput = () => {
    const [localAmount, setLocalAmount] = useState('');

    const handleAmountChange = useCallback((e) => {
      let value = e.target.value;
      
      // Remove tudo exceto números
      let numbers = value.replace(/\D/g, '');
      
      // Se estiver vazio, define como vazio
      if (numbers === '') {
        setLocalAmount('');
        return;
      }
      
      // Converte para número e formata
      let number = parseInt(numbers, 10) / 100;
      
      // Formata como moeda brasileira
      const formattedValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(number);
      
      setLocalAmount(formattedValue);
    }, []);

    const handleFocus = useCallback((e) => {
      if (localAmount === 'R$ 0,00') {
        setLocalAmount('');
      }
    }, [localAmount]);

    const handleBlur = useCallback((e) => {
      if (!localAmount || localAmount === 'R$') {
        setLocalAmount('R$ 0,00');
      }
    }, [localAmount]);

    return (
      <input 
        type="text"
        placeholder="R$ 0,00"
        value={localAmount}
        onChange={handleAmountChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={styles.amountInput}
        ref={amountInputRef}
      />
    );
  };
  const StepIndicator = () => (
    <div style={styles.stepIndicator}>
      <div style={{
        ...styles.stepCircle,
        ...(currentStep >= 1 ? styles.stepCircleActive : styles.stepCircleInactive)
      }}>1</div>
      <div style={{
        ...styles.stepLine,
        ...(currentStep >= 2 ? styles.stepLineActive : {})
      }}></div>
      <div style={{
        ...styles.stepCircle,
        ...(currentStep >= 2 ? styles.stepCircleActive : styles.stepCircleInactive)
      }}>2</div>
      <div style={{
        ...styles.stepLine,
        ...(currentStep >= 3 ? styles.stepLineActive : {})
      }}></div>
      <div style={{
        ...styles.stepCircle,
        ...(currentStep >= 3 ? styles.stepCircleActive : styles.stepCircleInactive)
      }}>3</div>
    </div>
  );

  // =============================================
  // COMPONENTES DE RENDERIZAÇÃO
  // =============================================

  const renderQRScannerModal = () => {
    if (!showQRScanner) return null;

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.qrModalContent}>
          <div style={styles.modalHeader}>
            <FaQrcode style={styles.modalIcon} />
            <h3 style={styles.modalTitle}>Ler QR Code</h3>
            <button 
              onClick={closeQRScanner}
              style={styles.closeModalButton}
            >
              ×
            </button>
          </div>
          
          <div style={styles.qrScannerContainer}>
            <div style={styles.cameraPreview}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={styles.cameraVideo}
              />
              <div style={styles.qrOverlay}>
                <div style={styles.qrFrame} />
                <p style={styles.qrInstruction}>
                  Posicione o QR Code dentro do quadro
                </p>
              </div>
            </div>
            
            {cameraError && (
              <div style={styles.cameraError}>
                {cameraError}
              </div>
            )}
          </div>

          <div style={styles.qrModalActions}>
            <button 
              onClick={closeQRScanner}
              style={styles.cancelButton}
            >
              Cancelar
            </button>
            
          </div>
        </div>
      </div>
    );
  };

  // Modal de confirmação de pagamento
  const renderPaymentModal = () => {
    if (!showPaymentModal) return null;

    // Mapear método de pagamento para display
    const metodoDisplay = {
      'credit': 'Cartão de Crédito',
      'debit': 'Cartão de Débito', 
      'cash': 'Dinheiro'
    };

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.paymentModalContent}>
          <div style={styles.paymentModalHeader}>
            <FaExclamationTriangle style={styles.warningIcon} />
            <h3 style={styles.paymentModalTitle}>CONFIRMAÇÃO DE PAGAMENTO</h3>
            <button 
              onClick={() => setShowPaymentModal(false)}
              style={styles.closeModalButton}
            >
              ×
            </button>
          </div>
          
          <div style={styles.paymentModalBody}>
            <div style={styles.warningSection}>
              <p style={styles.warningText}>
                <strong>ATENÇÃO: NÃO ESQUEÇA DE PROCESSAR O PAGAMENTO NA MAQUININHA</strong>
              </p>
              
              <div style={styles.memberInfo}>
                <FaUser style={styles.memberIcon} />
                <div style={styles.memberDetails}>
                  <span style={styles.memberName}>{selectedMember?.name}</span>
                  <span style={styles.memberId}>ID: {selectedMember?.memberId}</span>
                  <span style={styles.paymentMethod}>
                    Método: {metodoDisplay[paymentMethod] || paymentMethod}
                  </span>
                </div>
              </div>

              <div style={styles.amountDisplay}>
                <span style={styles.amountLabel}>Valor a ser processado:</span>
                <span style={styles.amountValue}>{processedAmount}</span>
              </div>
            </div>

            <div style={styles.paymentInstructions}>
              <h4 style={styles.instructionsTitle}>Siga estes passos:</h4>
              <p style={styles.instructionStep}>1️⃣ Digite o valor <strong>{processedAmount}</strong> na maquininha</p>
              <p style={styles.instructionStep}>2️⃣ Processe o pagamento do cliente</p>
              <p style={styles.instructionStep}>3️⃣ Aguarde a confirmação na maquininha</p>
              <p style={styles.instructionStep}>4️⃣ Clique em "PAGAMENTO CONFIRMADO" apenas após o pagamento ser aprovado</p>
            </div>
          </div>

          <div style={styles.paymentModalActions}>
            <button 
              onClick={() => setShowPaymentModal(false)}
              style={styles.cancelPaymentButton}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button 
              onClick={handlePaymentConfirmed}
              style={styles.confirmPaymentButton}
              disabled={isLoading}
            >
              {isLoading ? 'PROCESSANDO...' : `PAGAMENTO DE ${processedAmount} CONFIRMADO`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal de status TEF (PinPad)
  const renderTefModal = () => {
    if (!tefStatus || tefStatus === 'Creditado') return null;

    const statusMessages = {
      'iniciando': 'Iniciando transação no PinPad...',
      'aguardando_pinpad': 'Aguardando pagamento no PinPad...',
      'Pendente': 'Aguardando pagamento no PinPad...',
      'Em Pagamento': 'Processando no PinPad...',
      'EmPagamento': 'Processando no PinPad...',
      'erro': 'Erro ao iniciar transação',
    };

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.paymentModalContent}>
          <div style={styles.paymentModalHeader}>
            <h3 style={styles.paymentModalTitle}>RECARGA VIA PINPAD</h3>
          </div>

          <div style={styles.paymentModalBody}>
            <div style={styles.warningSection}>
              <div style={styles.memberInfo}>
                <FaUser style={styles.memberIcon} />
                <div style={styles.memberDetails}>
                  <span style={styles.memberName}>{selectedMember?.name}</span>
                  <span style={styles.paymentMethod}>
                    Método: {paymentMethod === 'pix' ? 'PIX' : paymentMethod === 'debit' ? 'Débito' : 'Crédito'}
                  </span>
                </div>
              </div>

              <div style={styles.amountDisplay}>
                <span style={styles.amountLabel}>Valor:</span>
                <span style={styles.amountValue}>{processedAmount}</span>
              </div>

              <p style={{ textAlign: 'center', marginTop: '16px', fontWeight: 'bold' }}>
                {statusMessages[tefStatus] || `Status: ${tefStatus}`}
              </p>
            </div>

            <div style={styles.paymentInstructions}>
              <h4 style={styles.instructionsTitle}>{tefOrigem === 'android' ? 'Na maquininha Android:' : 'No PIN PAD:'}</h4>
              <p style={styles.instructionStep}>1️⃣ Insira/aproxime o cartão</p>
              <p style={styles.instructionStep}>2️⃣ Digite a senha se solicitado</p>
              <p style={styles.instructionStep}>3️⃣ Aguarde a confirmação automática</p>
            </div>
          </div>

          <div style={styles.paymentModalActions}>
            <button 
              onClick={handleTefCancelar}
              style={styles.cancelPaymentButton}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMemberIdentification = () => {
    return (
      <div style={styles.memberIdentification}>
        <div style={styles.tagStatus}>
          <div style={styles.tagStatusIndicator}>
            <div style={{
              ...styles.statusLight,
              ...(isScanningTag ? styles.statusLightActive : styles.statusLightReady)
            }} />
            <span style={styles.statusText}>
              {isScanningTag ? 'Processando...' : 'Pronto para Leitura'}
            </span>
          </div>
          {tagData && (
            <div style={styles.tagData}>
              Última Tag: {tagData}
            </div>
          )}
        </div>

        <div style={styles.identificationOptions}>
          <div style={styles.instructions}>
            <p style={styles.instructionText}>
              <strong>Método 1 (RFID):</strong> Aproxime a carteirinha do leitor
            </p>
            <p style={styles.instructionText}>
              <strong>Método 2 (QR Code):</strong> Clique no botão da câmera para ler o QR Code
            </p>
          </div>

          <div style={styles.tagInputContainer}>
            <h4 style={styles.searchTitle}>Identificar Associado</h4>
            <div style={styles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Digite o ID da tag..."
                value={memberSearchQuery}
                onChange={(e) => {
                  setMemberSearchQuery(e.target.value);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && memberSearchQuery.trim().length > 0) {
                    handleTagDetected(memberSearchQuery);
                  }
                }}
                style={styles.tagInput}
                autoFocus
              />
              <button 
                onClick={handleQRCodeScan}
                style={styles.qrButton}
                title="Ler QR Code"
              >
                <FaQrcode />
              </button>
            </div>
          </div>

          
        </div>
      </div>
    );
  };

  const Step1Leitura = () => (
    <div>
      <StepIndicator />
      <div style={styles.scanContainer}>
        <h2 style={styles.title}>Identificação do Associado</h2>
        
        {renderMemberIdentification()}

        <div style={styles.scanInstructions}>
          <p><strong>Instruções para recarga:</strong></p>
          <p>1. Identifique o associado usando RFID ou QR Code</p>
          <p>2. Confirme os dados do associado</p>
          <p>3. Informe o valor da recarga</p>
          <p>4. Processe o pagamento na maquininha física</p>
        </div>
      </div>
    </div>
  );

  const Step2Confirmacao = () => {
    if (!associadoData) return null;
    
    return (
      <div>
        <StepIndicator />
        <button style={styles.backButton} onClick={handleBack}>
          <FaArrowLeft /> Voltar
        </button>
        
        <h2 style={styles.title}>Confirmação do Associado</h2>
        
        <div style={styles.confirmationCard}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Carteirinha de Associado</h2>
          </div>
          
          <div style={styles.cardBody}>
            <div style={styles.photoContainer}>
              <div style={styles.photoSpaceLarge}>
                {associadoData.foto ? (
                  <img 
                    src={`data:image/jpeg;base64,${associadoData.foto}`} 
                    alt="Foto do associado" 
                    style={styles.photo}
                  />
                ) : (
                  <div style={styles.photoPlaceholderLarge}>
                    <FaUser size={40} color="#999" />
                    <p style={{fontSize: '12px', marginTop: '5px'}}>Sem foto</p>
                  </div>
                )}
              </div>
            </div>
            
            <div style={styles.infoSection}>
              <div style={styles.infoContainer}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Nome:</span>
                  <span style={styles.infoValue}>{associadoData.nome}</span>
                </div>
                
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Cota:</span>
                  <span style={styles.infoValue}>{associadoData.cota}-{associadoData.digito}</span>
                </div>
                
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>CPF:</span>
                  <span style={styles.infoValue}>{associadoData.documento}</span>
                </div>

                {selectedMember && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Saldo Atual:</span>
                    <span style={styles.infoValue}>R$ {selectedMember.balance?.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div style={styles.cardFooter}>
            <p style={styles.footerText}>Associado identificado - Confirme os dados acima</p>
          </div>
        </div>
        
        <button 
          style={styles.continueButton}
          onClick={handleConfirmAssociado}
        >
          CONFIRMAR ASSOCIADO
        </button>
      </div>
    );
  };

  const Step3Pagamento = () => (
    <div>
      <StepIndicator />
      <button style={styles.backButton} onClick={handleBack}>
        <FaArrowLeft /> Voltar
      </button>
      
      <h1 style={styles.title}>Recarga de Saldo</h1>
      
      <div style={styles.paymentContainer}>
        <div style={styles.memberInfoSection}>
          <h4 style={styles.memberInfoTitle}>Associado Selecionado</h4>
          {selectedMember && (
            <div style={styles.memberInfo}>
              <FaUser style={styles.memberIcon} />
              <div style={styles.memberDetails}>
                <span style={styles.memberName}>{selectedMember.name}</span>
                <span style={styles.memberId}>ID: {selectedMember.memberId}</span>
                <span style={styles.memberBalance}>Saldo Atual: R$ {selectedMember.balance?.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        <div style={styles.step}>
          <span style={styles.stepNumber}><b>1.</b> </span>
          <span style={styles.stepText}>Processe o pagamento na maquininha física:</span>
        </div>
        
        <div style={styles.paymentMethods}>
          <div 
            style={{
              ...styles.paymentOption,
              ...(paymentMethod === 'credit' ? styles.paymentOptionSelected : {})
            }}
            onClick={() => handlePaymentMethodChange('credit')}
          >
            <input 
              type="radio" 
              id="credit" 
              name="paymentMethod" 
              checked={paymentMethod === 'credit'}
              onChange={() => {}}
              style={styles.radioInput}
            />
            <label htmlFor="credit">Cartão crédito</label>
          </div>
          
          <div 
            style={{
              ...styles.paymentOption,
              ...(paymentMethod === 'debit' ? styles.paymentOptionSelected : {})
            }}
            onClick={() => handlePaymentMethodChange('debit')}
          >
            <input 
              type="radio" 
              id="debit" 
              name="paymentMethod" 
              checked={paymentMethod === 'debit'}
              onChange={() => {}}
              style={styles.radioInput}
            />
            <label htmlFor="debit">Cartão débito</label>
          </div>
          
          <div 
            style={{
              ...styles.paymentOption,
              ...(paymentMethod === 'cash' ? styles.paymentOptionSelected : {})
            }}
            onClick={() => handlePaymentMethodChange('cash')}
          >
            <input 
              type="radio" 
              id="cash" 
              name="paymentMethod" 
              checked={paymentMethod === 'cash'}
              onChange={() => {}}
              style={styles.radioInput}
            />
            <label htmlFor="cash">Dinheiro</label>
          </div>

          <div 
            style={{
              ...styles.paymentOption,
              ...(paymentMethod === 'tef' ? styles.paymentOptionSelected : {})
            }}
            onClick={() => handlePaymentMethodChange('tef')}
          >
            <input 
              type="radio" 
              id="tef" 
              name="paymentMethod" 
              checked={paymentMethod === 'tef'}
              onChange={() => {}}
              style={styles.radioInput}
            />
            <label htmlFor="tef">PIX / TEF (PinPad)</label>
          </div>

          <div 
            style={{
              ...styles.paymentOption,
              ...(paymentMethod === 'tef_android' ? styles.paymentOptionSelected : {})
            }}
            onClick={() => handlePaymentMethodChange('tef_android')}
          >
            <input 
              type="radio" 
              id="tef_android" 
              name="paymentMethod" 
              checked={paymentMethod === 'tef_android'}
              onChange={() => {}}
              style={styles.radioInput}
            />
            <label htmlFor="tef_android">Maquininha (Android)</label>
          </div>
        </div>
        
        <div style={styles.step}>
          <span style={styles.stepNumber}><b>2.</b> </span>
          <span style={styles.stepText}>Valor da recarga do saldo:</span>
        </div>
        
        <div>
          <AmountInput />
        </div>

        <div style={styles.paymentInstructions}>
          <p><strong>Instruções:</strong></p>
          <p>1. Informe o valor da recarga acima</p>
          <p>2. Processe o pagamento na maquininha física</p>
          <p>3. Clique em "Finalizar Recarga" para confirmar</p>
        </div>
      </div>
      
      <button 
        style={{
          ...styles.continueButton,
          ...(isLoading ? styles.continueButtonDisabled : {})
        }}
        onClick={paymentMethod === 'tef' ? () => handleTefIniciar('pinpad') : paymentMethod === 'tef_android' ? () => handleTefIniciar('android') : handleContinue}
        disabled={isLoading}
      >
        {isLoading ? 'PROCESSANDO...' : paymentMethod === 'tef' ? 'ENVIAR PARA O PINPAD' : paymentMethod === 'tef_android' ? 'ENVIAR PARA MAQUININHA' : 'FINALIZAR RECARGA'}
      </button>
    </div>
  );

  return (
    <Layout title="Recarga de Saldo" icon={FaCashRegister}>
      <div style={styles.container}>
        {currentStep === 1 && <Step1Leitura />}
        {currentStep === 2 && <Step2Confirmacao />}
        {currentStep === 3 && <Step3Pagamento />}
        
        {/* Modal de QR Code */}
        {renderQRScannerModal()}
        
        {/* Modal de confirmação de pagamento */}
        {renderPaymentModal()}

        {/* Modal de status TEF (PinPad) */}
        {renderTefModal()}
      </div>
    </Layout>
  );
};

const styles = {
  container: {
    maxWidth: '500px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    minHeight: '500px',
    marginBottom: '40px'
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  title: {
    textAlign: 'center',
    color: '#333',
    marginBottom: '30px',
    fontSize: '24px',
    fontWeight: 'bold'
  },
  stepIndicator: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '30px',
    gap: '10px'
  },
  stepCircle: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  stepCircleActive: {
    backgroundColor: '#4CAF50',
    color: 'white'
  },
  stepCircleInactive: {
    backgroundColor: '#ddd',
    color: '#666'
  },
  stepLine: {
    width: '40px',
    height: '2px',
    backgroundColor: '#ddd'
  },
  stepLineActive: {
    backgroundColor: '#4CAF50'
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#09448C',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '20px',
    fontWeight: 'bold'
  },
  scanContainer: {
    textAlign: 'center',
    padding: '20px 0'
  },
  scanInstructions: {
    marginBottom: '30px',
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5',
    textAlign: 'left',
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  confirmationCard: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: '#fff',
    borderRadius: '15px',
    boxShadow: '0 10px 20px rgba(51, 51, 51, 0.3)',
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
    fontFamily: '"Montserrat", sans-serif',
    margin: '0 auto 30px'
  },
  cardHeader: {
    backgroundColor: '#2c3e50',
    color: '#fff',
    padding: '15px 20px',
    textAlign: 'center',
  },
  cardTitle: {
    margin: 0,
    fontSize: '1.4rem',
    fontWeight: '500',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
  },
  photoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '20px'
  },
  photoSpaceLarge: {
    width: '120px',
    height: '150px',
    backgroundColor: '#f5f5f5',
    border: '1px dashed #ccc',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '5px',
    overflow: 'hidden'
  },
  photoPlaceholderLarge: {
    color: '#999',
    fontSize: '14px',
    textAlign: 'center'
  },
  infoSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  infoContainer: {
    flex: 1,
  },
  infoRow: {
    marginBottom: '15px',
    display: 'flex',
    flexDirection: 'column',
  },
  infoLabel: {
    fontWeight: 'bold',
    color: '#2c3e50',
    marginRight: '10px',
    minWidth: '100px',
    marginBottom: '5px',
    fontSize: '14px'
  },
  infoValue: {
    color: '#333',
    wordBreak: 'break-word',
    fontSize: '14px'
  },
  cardFooter: {
    backgroundColor: '#ecf0f1',
    padding: '10px 20px',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
  },
  footerText: {
    margin: 0,
    color: '#7f8c8d',
    fontSize: '0.9rem',
  },
  paymentContainer: {
    marginBottom: '30px'
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '25px',
  },
  stepNumber: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '24px',
    height: '24px',
    backgroundColor: '#4CAF50',
    color: 'white',
    borderRadius: '50%',
    marginRight: '10px',
    fontWeight: 'bold'
  },
  stepText: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  paymentMethods: {
    marginBottom: '30px'
  },
  paymentOption: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 15px',
    marginBottom: '10px',
    borderRadius: '5px',
    cursor: 'pointer',
    border: '1px solid #ddd',
    transition: 'all 0.3s ease'
  },
  paymentOptionSelected: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4CAF50'
  },
  radioInput: {
    marginRight: '10px'
  },
  amountInput: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    marginBottom: '20px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '18px'
  },
  continueButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    fontFamily: 'Montserrat, sans-serif'
  },
  continueButtonDisabled: {
    backgroundColor: '#cccccc',
    cursor: 'not-allowed'
  },
  paymentInstructions: {
    backgroundColor: '#e8f5e9',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '1.5',
    marginTop: '20px'
  },
  note: {
    color: '#d32f2f',
    fontWeight: 'bold',
    fontStyle: 'italic'
  },

  // Estilos para identificação de membro
  memberIdentification: {
    marginBottom: '30px'
  },
  tagStatus: {
    backgroundColor: '#f5f5f5',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  tagStatusIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  statusLight: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
  },
  statusLightReady: {
    backgroundColor: '#4caf50',
    boxShadow: '0 0 10px #4caf50',
  },
  statusLightActive: {
    backgroundColor: '#ff9800',
    boxShadow: '0 0 10px #ff9800',
  },
  statusText: {
    fontWeight: '600',
    color: '#333',
  },
  tagData: {
    fontSize: '12px',
    color: '#666',
    fontFamily: 'monospace',
  },
  instructions: {
    backgroundColor: '#e8f5e8',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  instructionText: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#2e7d32',
  },
  tagInputContainer: {
    marginBottom: '20px',
  },
  searchTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },
  searchContainer: {
    display: 'flex',
    gap: '0',
  },
  tagInput: {
    flex: '1',
    padding: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px 0 0 8px',
    fontSize: '14px',
  },
  qrButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    border: 'none',
    padding: '12px 15px',
    borderRadius: '0 8px 8px 0',
    cursor: 'pointer',
    fontSize: '16px',
  },
  devButtons: {
    marginTop: '20px',
    textAlign: 'center',
  },
  testButton: {
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 auto',
  },

  // Estilos para informações do membro
  memberInfoSection: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  memberInfoTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  memberIcon: {
    fontSize: '20px',
    color: '#3f51b5',
  },
  memberDetails: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  memberName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  memberId: {
    fontSize: '12px',
    color: '#666',
  },
  memberBalance: {
    fontSize: '12px',
    color: '#4caf50',
    fontWeight: '500',
  },

  // Estilos do modal de QR Code
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  qrModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    position: 'relative',
  },
  modalIcon: {
    fontSize: '24px',
    color: '#4caf50',
    marginRight: '10px',
  },
  modalTitle: {
    color: '#3f51b5',
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
  },
  closeModalButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#757575',
    padding: 0,
    lineHeight: '1',
  },
  qrScannerContainer: {
    marginBottom: '20px',
  },
  cameraPreview: {
    position: 'relative',
    width: '100%',
    height: '300px',
    backgroundColor: '#000',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '15px',
  },
  cameraVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  qrOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrFrame: {
    width: '200px',
    height: '200px',
    border: '2px solid #4caf50',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  qrInstruction: {
    color: 'white',
    textAlign: 'center',
    fontSize: '14px',
    margin: 0,
    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
  },
  cameraError: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '10px',
    borderRadius: '4px',
    textAlign: 'center',
    fontSize: '14px',
  },
  qrModalActions: {
    display: 'flex',
    gap: '15px',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '1',
  },
  scanButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },

  // Estilos do modal de pagamento
  paymentModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  paymentModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    position: 'relative',
    backgroundColor: '#fff3cd',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #ffeaa7'
  },
  warningIcon: {
    fontSize: '28px',
    color: '#ff9800',
    marginRight: '15px',
  },
  paymentModalTitle: {
    color: '#856404',
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
  },
  paymentModalBody: {
    marginBottom: '25px',
  },
  warningSection: {
    backgroundColor: '#fff3cd',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #ffeaa7',
    marginBottom: '20px',
    textAlign: 'center'
  },
  warningText: {
    color: '#856404',
    fontSize: '18px',
    margin: '0 0 15px 0',
    lineHeight: '1.4'
  },
  amountDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    marginTop: '15px'
  },
  amountLabel: {
    fontSize: '16px',
    color: '#856404',
    fontWeight: 'bold'
  },
  amountValue: {
    fontSize: '28px',
    color: '#d32f2f',
    fontWeight: 'bold',
    backgroundColor: '#ffebee',
    padding: '10px 20px',
    borderRadius: '8px',
    border: '2px solid #f44336'
  },
  paymentInstructions: {
    backgroundColor: '#e8f5e9',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #c8e6c9'
  },
  instructionStep: {
    margin: '8px 0',
    fontSize: '14px',
    color: '#2e7d32',
    lineHeight: '1.4'
  },
  paymentModalActions: {
    display: 'flex',
    gap: '15px',
  },
  cancelPaymentButton: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    border: 'none',
    padding: '15px 20px',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '1',
  },
  confirmPaymentButton: {
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    padding: '15px 20px',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '2',
    textTransform: 'uppercase'
  },
};

// Adicionar animações CSS
const styleSheet = document.styleSheets[0];
const keyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

export default BalanceRecharge;